/**
 * Drills Helper Functions
 * Business logic for drill calculations and status determination
 *
 * FT-189: Display name consolidation
 * - Canonical field: `name`
 * - Legacy fields: `drill_type`, `drillType`
 * - Fallback: `type` (used in useDrills domain hook)
 */

// ============================================================================
// FT-189: DISPLAY NAME HELPERS - Centralized drill name resolution
// ============================================================================

/**
 * FT-189: Get drill record display name from a drill object
 * Canonical helper for consistent name resolution across the app.
 * Priority: name > drill_type > drillType > type > 'Unknown Drill'
 *
 * @param {Object|string} drill - Drill record object or type string
 * @returns {string} Display name for the drill
 */
export const getDrillRecordDisplayName = (drill) => {
  // Handle null/undefined
  if (!drill) return 'Unknown Drill';

  // If it's already a string (drill type code), use existing getDrillDisplayName
  if (typeof drill === 'string') {
    return getDrillDisplayName(drill);
  }

  // Priority chain for drill record objects: name > drill_type > drillType > type
  const drillTypeCode = (
    drill.name ||
    drill.drill_type ||
    drill.drillType ||
    drill.type ||
    ''
  ).toString().trim();

  if (!drillTypeCode) return 'Unknown Drill';

  // If it looks like a human-readable name (has spaces or is title-cased), return as-is
  if (drillTypeCode.includes(' ') || /^[A-Z][a-z]/.test(drillTypeCode)) {
    return drillTypeCode;
  }

  // Otherwise, convert drill type code to display name
  return getDrillDisplayName(drillTypeCode);
};

/**
 * FT-189: Resolve drill name from various input types
 * Handles: drill objects, drill type strings, or drill IDs
 *
 * @param {Object|string} drillInput - Drill object, type string, or ID
 * @param {Array} drillList - Optional list of drills for ID lookup
 * @returns {string} Resolved display name
 */
export const resolveDrillName = (drillInput, drillList = []) => {
  // Handle null/undefined
  if (!drillInput) return 'Unknown Drill';

  // If it's an object, use getDrillRecordDisplayName
  if (typeof drillInput === 'object') {
    return getDrillRecordDisplayName(drillInput);
  }

  // If it's a string
  if (typeof drillInput === 'string') {
    const trimmed = drillInput.trim();

    // If it looks like an ID (has "drill" prefix or UUID pattern), try to find in list
    if (trimmed.startsWith('drill') || trimmed.includes('-')) {
      const found = drillList.find(d => d.id === trimmed);
      if (found) {
        return getDrillRecordDisplayName(found);
      }
    }

    // Otherwise treat as drill type code
    return getDrillDisplayName(trimmed);
  }

  return 'Unknown Drill';
};

// ============================================================================
// DRILL STATUS HELPERS
// ============================================================================

/**
 * Calculate drill status based on requirement and drill history
 */
export const calculateDrillStatus = (requirement, drillsOfType) => {
  if (!drillsOfType || drillsOfType.length === 0) {
    return {
      status: 'overdue',
      daysUntilDue: -requirement.frequency_days,
      lastDrillDate: null
    };
  }

  // Sort drills by date (most recent first)
  const sortedDrills = [...drillsOfType].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const lastDrill = sortedDrills[0];
  const lastDrillDate = new Date(lastDrill.date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  lastDrillDate.setHours(0, 0, 0, 0);

  // Calculate days since last drill
  const daysSinceLastDrill = Math.floor((today - lastDrillDate) / (1000 * 60 * 60 * 24));
  const daysUntilDue = requirement.frequency_days - daysSinceLastDrill;

  let status;
  if (daysUntilDue < 0) {
    status = 'overdue';
  } else if (daysUntilDue <= 7) {
    status = 'due_soon';
  } else {
    status = 'compliant';
  }

  return {
    status,
    daysUntilDue,
    lastDrillDate: lastDrill.date,
    daysSinceLastDrill
  };
};

/**
 * Format days until due for display
 */
export const formatDaysUntilDue = (days) => {
  // Handle null/undefined/NaN cases
  if (days == null || isNaN(days)) {
    return 'Not scheduled';
  }
  
  if (days < 0) {
    return `${Math.abs(days)} days overdue`;
  } else if (days === 0) {
    return 'Due today';
  } else if (days === 1) {
    return 'Due tomorrow';
  } else if (days <= 7) {
    return `Due in ${days} days`;
  } else {
    return `Due in ${days} days`;
  }
};

/**
 * Get drill type display name
 */
export const getDrillDisplayName = (drillType) => {
  // FT-01 FIX: Null guard to prevent crash when drillType is undefined
  if (!drillType) return 'Unknown Drill';

  const displayNames = {
    'fire': 'Fire Drill',
    'abandon_ship': 'Abandon Ship Drill',
    'mob': 'Man Overboard Drill',
    'pollution_response': 'Pollution Response Drill',
    'gmdss_dsc': 'GMDSS/DSC Test',
    'medical_emergency': 'Medical Emergency Drill',
    'flooding': 'Flooding Drill',
    'steering_nav_fail': 'Steering/Navigation Failure',
    'electrical_blackout': 'Electrical Blackout',
    'enclosed_space_entry': 'Enclosed Space Entry',
    'towing_emergency': 'Towing Emergency',
    'collision': 'Collision Drill',
    'grounding': 'Grounding Drill'
  };

  return displayNames[drillType] || (drillType || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Drill';  // FT-387: guard undefined drillType
};

/**
 * Get drill icon based on type
 */
export const getDrillIcon = (drillType) => {
  const icons = {
    'fire': 'Flame',
    'abandon_ship': 'Ship',
    'mob': 'LifeBuoy',
    'pollution_response': 'Droplet',
    'medical_emergency': 'Heart',
    'flooding': 'Waves',
    'collision': 'AlertTriangle',
    'electrical_blackout': 'Zap'
  };

  return icons[drillType] || 'AlertCircle';
};

/**
 * Calculate compliance percentage
 */
export const calculateCompliancePercentage = (drills, requirements) => {
  if (!requirements || requirements.length === 0) return 100;

  const requiredDrills = requirements.filter(r => r.compliance_level === 'Required');
  if (requiredDrills.length === 0) return 100;

  let compliantCount = 0;
  requiredDrills.forEach(requirement => {
    const drillsOfType = drills.filter(d => d.type === requirement.drill_type);
    const status = calculateDrillStatus(requirement, drillsOfType);
    if (status.status === 'compliant') {
      compliantCount++;
    }
  });

  return Math.round((compliantCount / requiredDrills.length) * 100);
};

/**
 * Sort drills by urgency
 */
export const sortDrillsByUrgency = (drillStatuses) => {
  return [...drillStatuses].sort((a, b) => {
    // Sort by status priority first
    const statusPriority = { 'overdue': 0, 'due_soon': 1, 'compliant': 2 };
    if (statusPriority[a.status] !== statusPriority[b.status]) {
      return statusPriority[a.status] - statusPriority[b.status];
    }

    // Then by days until due
    return a.daysUntilDue - b.daysUntilDue;
  });
};

/**
 * Calculate drill compliance stats from drillStatuses array
 * Industry-standard compliance = (total - overdue) / total × 100
 * "Due Soon" drills are NOT overdue, so they count as compliant.
 * This matches MCA/ISM/BIMCO KPIs where only OVERDUE items are non-compliant.
 *
 * This is the SINGLE SOURCE OF TRUTH for drill compliance calculations.
 * Used by both Dashboard (useDashboardCompliance) and Module (SafetyDrillsTab).
 *
 * @param {Array} drillStatuses - Array of drill objects with status field
 * @returns {Object} Stats object with overdue, dueSoon, compliant, total, compliance
 */
export const calculateDrillStats = (drillStatuses = []) => {
  // Filter to required drills only
  const requiredDrills = drillStatuses.filter(d =>
    d.complianceLevel === 'Required' ||
    d.compliance_level === 'Required' ||
    d.required === true
  );

  const overdue = requiredDrills.filter(d => d.status === 'overdue').length;
  const dueSoon = requiredDrills.filter(d => d.status === 'due_soon').length;
  const compliant = requiredDrills.filter(d => d.status === 'compliant').length;
  const total = requiredDrills.length;

  // Only OVERDUE drills count as non-compliant; "Due Soon" is still compliant
  const compliance = total > 0
    ? Math.round(((total - overdue) / total) * 100)
    : 100;

  return {
    overdue,
    dueSoon,
    compliant,
    total,
    compliance
  };
};